/**
 * iOS deep-link fallback for Courier App links.
 *
 * Links matching `{domain}/linking/{slug}` are Universal Links for the Courier App.
 * On iOS, when a Universal Link is clicked from a page on the same domain,
 * the user can be bounced to the App Store instead of the app opening.
 *
 * To work around this, on iOS we intercept those links and first try the app's custom
 * URL scheme, e.g. `com.justeattakeaway.courier.uk://linking/{slug}`,
 * and fall back to the original HTTPS URL if the app isn't installed.
 *
 * Android and desktop are untouched: the listener is only attached on iOS, so those
 * platforms keep using the HTTPS link exactly as before.
 *
 * @module deeplinkFallback
 */

/** Matches `/linking/{slug}` (any non-empty slug). */
const LINKING_PATH_PATTERN = /^\/linking\/.+/;

// How long to wait for the app to open before falling back to the HTTPS link.
const APP_OPEN_TIMEOUT_MS = 1200;

/**
 * @returns {boolean} true on iOS devices (incl. iPadOS 13+, which should be detected as Mac).
 */
function isIOS() {
    const ua = window.navigator.userAgent;
    const isIOSDevice = /iPad|iPhone|iPod/.test(ua);
    const isIPadOS = window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1;

    return isIOSDevice || isIPadOS;
}

/**
 * The brand -> scheme map is defined in `config/app.php` (`courier_app_schemes`) and
 * injected as `window.courierAppSchemes`. Countries not listed fall back to HTTPS.
 *
 * @returns {string|undefined} the configured custom scheme for the current brand, if any.
 */
function resolveScheme() {
    const countryCode = window.country && window.country.code;
    const schemes = window.courierAppSchemes || {};

    return countryCode ? schemes[countryCode] : undefined;
}

/**
 * Build the custom-scheme URL, preserving the original path (slug) and any query string.
 *
 * @param {string} scheme
 * @param {URL} url
 * @returns {string}
 */
function buildSchemeUrl(scheme, url) {
    return `${scheme}://${url.pathname.replace(/^\//, '')}${url.search}${url.hash}`;
}

/**
 * Try to open the app via its custom scheme; if the page never backgrounds
 * (i.e. the app didn't open), navigate to the original HTTPS URL.
 *
 * @param {string} schemeUrl
 * @param {string} httpsFallbackUrl
 */
function openInAppWithFallback(schemeUrl, httpsFallbackUrl) {
    let pageHidden = false;

    const onVisibilityChange = () => {
        if (document.visibilityState === 'hidden') {
            pageHidden = true;
        }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);

    const fallbackTimer = window.setTimeout(() => {
        document.removeEventListener('visibilitychange', onVisibilityChange);

        // App didn't take over -> use the original HTTPS link.
        if (!pageHidden) {
            window.location.href = httpsFallbackUrl;
        }
    }, APP_OPEN_TIMEOUT_MS);

    // If the app opened (or we navigated), cancel the fallback.
    window.addEventListener(
        'pagehide',
        () => {
            window.clearTimeout(fallbackTimer);
            document.removeEventListener('visibilitychange', onVisibilityChange);
        },
        { once: true },
    );

    window.location.href = schemeUrl;
}

/**
 * Global, delegated handler: catches every `/linking/{slug}` link, current or future
 *
 * @param {MouseEvent} event
 */
function handleLinkingClick(event) {
    if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
    ) {
        return;
    }

    if (!(event.target instanceof Element)) return;

    const anchor = event.target.closest('a[href]');

    if (!anchor) return;

    let url;

    try {
        url = new URL(anchor.href, window.location.origin);
    } catch {
        return;
    }

    // Only handle links with our own `/linking/{slug}` path.
    if (url.origin !== window.location.origin) return;
    if (!LINKING_PATH_PATTERN.test(url.pathname)) return;

    const scheme = resolveScheme();

    // No scheme configured for this brand -> leave the original HTTPS link untouched.
    if (!scheme) return;

    event.preventDefault();
    openInAppWithFallback(buildSchemeUrl(scheme, url), anchor.href);
}

/**
 * Attach the global handler, iOS only.
 */
export function initDeeplinkFallback() {
    if (!isIOS()) return;

    document.addEventListener('click', handleLinkingClick, true);
}
